'use client'

import { AlertCircle, Eye, EyeOff, Lock, User } from 'lucide-react'
import type React from 'react'
import { Fragment, useState } from 'react'
import { useAdminAuth } from '@/stores/auth'
import { resolveNextOrFallback } from '@/lib/redirects'
import { Link, useRouter } from '@/i18n/navigation'
import { useParams, useSearchParams } from 'next/navigation'
import { useForm } from 'react-hook-form'
import { Form, FormField } from '@/components/ui/form'

interface LoginFormProps {
  // Define any props if needed
}

interface LoginFormState {
  username: string
  password: string
}

export function LoginForm({}: LoginFormProps) {
  const form = useForm<LoginFormState>({
    mode: 'onChange',
    defaultValues: {
      username: '',
      password: '',
    },
  })

  const [showPassword, setShowPassword] = useState(false)
  const [pageError, setPageError] = useState<string | null>(null)

  const { login, isLoading } = useAdminAuth()
  const router = useRouter()
  const params = useParams()
  const locale = params.locale as string
  const searchParams = useSearchParams()
  const nextParam = searchParams?.get('next') || null

  const handleSubmit = async (data: LoginFormState) => {
    setPageError(null)
    try {
      const { username, password } = data
      const result = await login(username, password)

      if (result?.success === false) {
        setPageError(result?.error || 'Login failed')
        return
      }

      if (result?.success === true) {
        const resolved = resolveNextOrFallback(nextParam, locale)
        router.replace(resolved)
        return
      }

      setPageError('Unexpected response from server')
    } catch (err: any) {
      setPageError(err?.message || 'Network error occurred')
    }
  }

  return (
    <div className="bg-white py-8 px-6 shadow-lg rounded-lg text-black">
      <Form {...form}>
        <form className="space-y-6" onSubmit={form.handleSubmit(handleSubmit)}>
          {pageError && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4 flex items-center space-x-2">
              <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
              <span className="text-sm text-red-700">{pageError}</span>
            </div>
          )}

          <FormField
            control={form.control}
            name="username"
            render={({ field }) => {
              return (
                <div>
                  <label
                    htmlFor="username"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Username
                  </label>
                  <div className="mt-1 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <User className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="username"
                      type="text"
                      required
                      {...field}
                      className="appearance-none block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      placeholder="Enter your username"
                      disabled={isLoading}
                    />
                  </div>
                </div>
              )
            }}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => {
              return (
                <div>
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Password
                  </label>
                  <div className="mt-1 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      required
                      {...field}
                      className="appearance-none block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      placeholder="Enter your password"
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={isLoading}
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                      ) : (
                        <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                      )}
                    </button>
                  </div>
                </div>
              )
            }}
          />

          <div className="flex justify-end">
            <Link
              href="/admin/reset-password/request"
              data-testid="forgot-password-link"
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Forgot password?
            </Link>
          </div>

          <div>
            <button
              type="submit"
              disabled={isLoading || !form.formState.isValid}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  Signing in...
                </div>
              ) : (
                'Sign in'
              )}
            </button>
          </div>
        </form>
      </Form>

      <div className="mt-6 p-4 bg-gray-50 rounded-md">
        <p className="text-xs text-gray-600 text-center" />
      </div>
    </div>
  )
}
